import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

// How a lab report was checked, from the browser (TGCLOUD-1868, plan D7): one session per opening
// of the verification view, for the service leads' report of accept-without-edit (C2).
//
//   const { sessionId, counters, finish } = useVerificationSession({ token, origin });
//
// THE SESSION. It starts when there is a reading to check: given the reading's signed `token`
// (intake's answer) and where the view was opened from (`origin`: 'upload', 'inbox' or 'saved'), it
// POSTs /test-results/verification-sessions. `sessionId` is null until the server answers; the Test
// save sends it (labReportApplication.sessionId) so the server can say the review was saved. It
// finishes ONCE: `finish(outcome)` ('saved', 'closed', 'sent-to-inbox', 'refused') PUTs the counts,
// and unmounting, or a new token, finishes an unfinished session as 'closed', or as 'refused' when
// the last save of it was refused (`counters.saveRefused()`, TestForm's; TGCLOUD-1868 review: the
// view was only ever finished 'saved', 'sent-to-inbox' or 'closed', so the report never showed a
// refused save). A page that goes away mid-review (`pagehide`: the tab closed, the browser left)
// finishes it too, over a request that outlives the page (fetch `keepalive`, since the finish is a
// PUT and a beacon can only POST), so no session stays open for good.
//
// A SAVED report shown again ("Open report", plan D6) has no reading's token, and is not measured:
// origin 'saved' is in the vocabulary and nothing starts one (a decision for Tim, TGCLOUD-1868 review).
//
// THE COUNTS. `counters.anchorOpened()` each time a value is shown in the report or the report
// shows its value; `valueEdited()` each change to a value read from the report; `valueTyped(differed)`
// each low-confidence value entered by hand, and whether it differed from the reading;
// `groupConfirmed()` each group confirmed; `setGroupsTotal(n)` how many groups there are to confirm.
// The server clamps confirmations to the total.
//
// VISIBLE TIME is time the page was visible (document.visibilityState) AND the window had focus.
//
// BEST EFFORT AND SILENT. Telemetry never gets in the way of checking a report: a request that
// fails is dropped, nothing is shown, nothing is retried, and `finish` always resolves. Nothing
// about the report or the patient is sent: counts, a time and an outcome.

export const VERIFICATION_ORIGINS = Object.freeze(['upload', 'inbox', 'saved']);
export const VERIFICATION_OUTCOMES = Object.freeze([
  'saved',
  'closed',
  'sent-to-inbox',
  'refused'
]);

const emptyCounts = () => ({
  anchorsOpened: 0,
  valuesEdited: 0,
  valuesTyped: 0,
  typedDiffered: 0,
  groupsConfirmed: 0,
  groupsTotal: 0
});

const pageVisible = () =>
  typeof document === 'undefined' ||
  document.visibilityState === undefined ||
  document.visibilityState === 'visible';

const windowFocused = () =>
  typeof document === 'undefined' || typeof document.hasFocus !== 'function'
    ? true
    : document.hasFocus();

// The clock: visible time so far, and since when it has been running (null while stopped).
function settle(clock, now) {
  if (clock.since !== null) {
    clock.visibleMs += Math.max(0, now - clock.since);
  }
  clock.since = clock.running && clock.visible && clock.focused ? now : null;
}

const sessionPath = (id) =>
  APICONSTANTS.labReportVerificationSession.replace(
    ':id',
    encodeURIComponent(id)
  );

// The finish of a page going away: a PUT that outlives it (fetch keepalive), against the same base
// as the axios instance, with the session's cookies. Best effort, as everything here.
function putOnUnload(id, body) {
  try {
    if (typeof fetch !== 'function') return Promise.resolve();
    return fetch(`${axios.defaults?.baseURL || ''}${sessionPath(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      keepalive: true
    }).then(
      () => undefined,
      () => undefined
    );
  } catch (_err) {
    return Promise.resolve();
  }
}

function finishSession(session, outcome, { unloading = false } = {}) {
  if (session.finished) return session.done;
  session.finished = true;
  session.clock.running = false;
  settle(session.clock, Date.now());
  // Closed after a refused save: refused (see the header).
  const said = outcome === 'closed' && session.refused ? 'refused' : outcome;
  const body = {
    ...session.counts,
    visibleMs: Math.round(session.clock.visibleMs),
    outcome: VERIFICATION_OUTCOMES.includes(said) ? said : 'closed'
  };
  // Going away with the id already known: sent at once, before the page is gone.
  if (unloading && session.id) {
    session.done = putOnUnload(session.id, body);
    return session.done;
  }
  session.done = Promise.resolve(session.started)
    .then((id) =>
      id
        ? unloading
          ? putOnUnload(id, body)
          : axios.put(sessionPath(id), body)
        : null
    )
    .then(
      () => undefined,
      () => undefined
    );
  return session.done;
}

/**
 * @param {{ token?: string|null, origin?: string }} args
 * @returns {{ sessionId: string|null, counters: object, finish: (outcome: string) => Promise<void> }}
 */
export default function useVerificationSession({ token, origin } = {}) {
  const [sessionId, setSessionId] = useState(null);
  const current = useRef(null);

  useEffect(() => {
    // A new reading, or none: the last session's id is never sent with another review's save.
    setSessionId(null);
    if (!token) return undefined;
    const clock = {
      visibleMs: 0,
      since: null,
      running: true,
      visible: pageVisible(),
      focused: windowFocused()
    };
    settle(clock, Date.now());
    const session = {
      finished: false,
      counts: emptyCounts(),
      clock,
      started: null,
      // The server's id once it answers, for a page that goes away before a promise can run.
      id: null,
      // The last save of this review was refused (counters.saveRefused).
      refused: false,
      done: null
    };
    current.current = session;

    // Inside a promise, so even a request that throws as it is made only ends the session.
    session.started = Promise.resolve()
      .then(() =>
        axios.post(APICONSTANTS.labReportVerificationSessions, {
          token,
          origin
        })
      )
      .then(
        (res) => {
          const id = res?.data?.sessionId;
          if (typeof id !== 'string' || !id) return null;
          session.id = id;
          if (current.current === session) setSessionId(id);
          return id;
        },
        () => null
      );

    const onVisibility = () => {
      clock.visible = pageVisible();
      settle(clock, Date.now());
    };
    const onFocus = () => {
      clock.focused = true;
      settle(clock, Date.now());
    };
    const onBlur = () => {
      clock.focused = false;
      settle(clock, Date.now());
    };
    // The page going away mid-review: finished as closed (or refused), over a request that outlives it.
    const onPageHide = () =>
      finishSession(session, 'closed', { unloading: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
      if (current.current === session) current.current = null;
      finishSession(session, 'closed');
    };
  }, [token, origin]);

  const counters = useMemo(() => {
    const bump = (field, by = 1) => {
      const session = current.current;
      if (session && !session.finished) session.counts[field] += by;
    };
    return {
      anchorOpened: () => bump('anchorsOpened'),
      valueEdited: () => bump('valuesEdited'),
      valueTyped: (differed) => {
        bump('valuesTyped');
        if (differed === true) bump('typedDiffered');
      },
      groupConfirmed: () => bump('groupsConfirmed'),
      // A save of this review refused: closed now, it is recorded as refused.
      saveRefused: () => {
        const session = current.current;
        if (session && !session.finished) session.refused = true;
      },
      setGroupsTotal: (n) => {
        const session = current.current;
        if (session && !session.finished && Number.isFinite(n) && n >= 0) {
          session.counts.groupsTotal = Math.round(n);
        }
      }
    };
  }, []);

  const finish = useCallback(
    (outcome) =>
      current.current
        ? finishSession(current.current, outcome)
        : Promise.resolve(),
    []
  );

  return { sessionId, counters, finish };
}
